// =============================================================================
// useConversationPreviews — shared foundation for DM swipe + neighbor-preview nav
// (plan dm-swipe-nav, F1 + F4).
//
// One place owns: the per-conversation bundle fetch (the SAME two queries
// ConversationViewModal runs, so a prefetch also WARMS the reader's message
// cache), the derivation of the preview card's fields, and the neighbor
// prefetch. Every DM surface adapts its list row into ONE PreviewData shape so
// no surface re-invents the card (CLAUDE.md #10).
//
// conversationId everywhere here = the ghl_conversations.id UUID (the reader
// queries .eq('id', conversationId)), NOT ghl_conversation_id.
// =============================================================================
import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { setMessagesCache, MESSAGES_TTL } from "../../lib/setterPerformanceCacheService";

// Mirrors ConversationViewModal.tsx:154 (the reader's hard page cap). Kept in
// sync by value; if the reader ever changes its cap, change this comment's anchor.
// Exported so the preview card renders the same "N+" overflow cap.
export const MSG_PAGE_SIZE = 200;

/** A ghl_messages row, the columns the reader selects (CTX-mechanism-probe). */
export interface BundleMessage {
  id: string;
  message_text: string | null;
  sent_at: string;
  sender_type: string;
  has_media: boolean | null;
  ghl_sender_user_id: string | null;
  sender_name: string | null;
  message_type: string | null;
}

/** The raw fetch result for one conversation. */
export interface ConversationBundle {
  messages: BundleMessage[];
  replyTurns: number | null;
  setterName: string | null;
  lastLeadMsgAt: string | null;
  lastSetterMsgAt: string | null;
}

/**
 * The single normalized shape the preview card + reader consume. `null` = a
 * field is unknown until prefetch (renders a shimmer). name + handle are always
 * known from the sibling list row, so they are never null.
 */
export interface PreviewData {
  conversationId: string;
  contactName: string;
  leadInstagram: string | null;
  replyTurns: number | null;
  messageCount: number | null;
  waitLabel: string | null;
  lastMessageText: string | null;
  whoLast: "you" | "lead" | "automated" | null;
  lastMessageAt: string | null;
  setterName: string | null;
}

/** A sibling list row carries only these three fields on most surfaces. */
export interface SiblingRef {
  conversationId: string;
  contactName: string;
  leadInstagram?: string | null;
}

/**
 * The nav-props sibling shape (F2). Superset of SiblingRef: a surface whose list
 * row already carries the message-derived card fields (P2 SessionMessages.groups,
 * P4 WinnerConversation) attaches them as `initialPreview` so the card renders
 * them WITHOUT a ghl_messages fetch — the prefetch then only fills the metadata
 * (reply turns, setter, wait) via the meta-only path (no ghl_messages hit).
 */
export interface NavSibling extends SiblingRef {
  initialPreview?: Partial<PreviewData>;
}

/** The optional nav payload a list surface threads up to the CVM open site so
 *  the reader gets its neighbor set (P1-P6). `siblings` is a snapshot in screen
 *  order; `hasMore`/`loadMore` are the source list's pager (F5). */
export interface ConversationNav {
  siblings: NavSibling[];
  index: number;
  hasMore: boolean;
  loadMore?: () => void;
}

/** Metadata-only fetch result — the fields NOT derivable from an in-hand
 *  messages array (reply turns, setter name, the wait-timestamp pair). */
export interface ConversationMeta {
  replyTurns: number | null;
  setterName: string | null;
  lastLeadMsgAt: string | null;
  lastSetterMsgAt: string | null;
}

// -----------------------------------------------------------------------------
// Module bundle cache — keyed `${organizationId}:${conversationId}`, TTL shared
// with the reader's own messages cache (import MESSAGES_TTL, never a 2nd number).
// -----------------------------------------------------------------------------
const previewCache = new Map<string, { bundle: ConversationBundle; at: number }>();
const metaCache = new Map<string, { meta: ConversationMeta; at: number }>();
const cacheKey = (organizationId: string, conversationId: string) =>
  `${organizationId}:${conversationId}`;

// Subscription — the module Maps are not reactive, so a card that read a MISS
// needs to re-render once the debounced prefetch (useNeighborPrefetch) fills the
// cache. A fetch write notifies every card subscribed to that conversation.
const listeners = new Map<string, Set<() => void>>();
function subscribePreview(
  organizationId: string,
  conversationId: string,
  cb: () => void
): () => void {
  const key = cacheKey(organizationId, conversationId);
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(cb);
  return () => {
    set!.delete(cb);
    if (set!.size === 0) listeners.delete(key);
  };
}
function notifyPreview(organizationId: string, conversationId: string): void {
  const set = listeners.get(cacheKey(organizationId, conversationId));
  if (set) for (const cb of set) cb();
}

export function getCachedBundle(
  organizationId: string,
  conversationId: string
): ConversationBundle | null {
  const key = cacheKey(organizationId, conversationId);
  const hit = previewCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > MESSAGES_TTL) {
    previewCache.delete(key);
    return null;
  }
  return hit.bundle;
}

export function getCachedMeta(
  organizationId: string,
  conversationId: string
): ConversationMeta | null {
  const key = cacheKey(organizationId, conversationId);
  const hit = metaCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > MESSAGES_TTL) {
    metaCache.delete(key);
    return null;
  }
  return hit.meta;
}

/** Drop a conversation's cached bundle + meta — called by resync + dismiss (F6)
 *  so a stale card can never reappear. */
export function invalidatePreviewBundle(conversationId: string, organizationId: string): void {
  const key = cacheKey(organizationId, conversationId);
  previewCache.delete(key);
  metaCache.delete(key);
}

// -----------------------------------------------------------------------------
// Pure helpers (unit-tested, no network)
// -----------------------------------------------------------------------------

/** whoLast per CLAUDE.md #6: walk BACKWARD from newest, skip ManyChat rows
 *  (setter with a null ghl_sender_user_id), take the first human row. */
export function deriveFromMessages(messages: BundleMessage[]): {
  messageCount: number;
  lastMessageText: string | null;
  lastMessageAt: string | null;
  whoLast: "you" | "lead" | "automated";
} {
  const last = messages[messages.length - 1];
  let whoLast: "you" | "lead" | "automated" = "automated";
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.sender_type === "setter" && m.ghl_sender_user_id == null) continue; // ManyChat bot
    whoLast = m.sender_type === "setter" ? "you" : "lead";
    break;
  }
  return {
    messageCount: messages.length,
    lastMessageText: last?.message_text ?? null,
    lastMessageAt: last?.sent_at ?? null,
    whoLast,
  };
}

/** Whole-minute elapsed duration between two absolute UTC instants. Timezone-
 *  agnostic (a gap, not a wall-clock render) — no org-tz handling applies. */
export function minutesSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
}

/** 'Nh Nm' (>=60) / 'Nm' (1-59) / '<1m' (0). No seconds branch — minutesSince
 *  floors, so a sub-minute wait renders '<1m'. */
export function formatWait(minutes: number): string {
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** A PreviewData carrying only the always-known identity fields; every metric is
 *  null (renders a shimmer) until a surface's initial or a prefetch fills it. */
export function identityPreview(sibling: SiblingRef): PreviewData {
  return {
    conversationId: sibling.conversationId,
    contactName: sibling.contactName,
    leadInstagram: sibling.leadInstagram ?? null,
    replyTurns: null,
    messageCount: null,
    waitLabel: null,
    lastMessageText: null,
    whoLast: null,
    lastMessageAt: null,
    setterName: null,
  };
}

/** Overlay `incoming` onto `base`, filling ONLY the fields still null on `base`
 *  (F4 MERGE rule). A surface's own authoritative field is never clobbered by a
 *  later re-derivation. Identity fields (conversationId/contactName) stay base's. */
export function mergeFillNulls(base: PreviewData, incoming: Partial<PreviewData>): PreviewData {
  return {
    conversationId: base.conversationId,
    contactName: base.contactName,
    leadInstagram: base.leadInstagram ?? incoming.leadInstagram ?? null,
    replyTurns: base.replyTurns ?? incoming.replyTurns ?? null,
    messageCount: base.messageCount ?? incoming.messageCount ?? null,
    waitLabel: base.waitLabel ?? incoming.waitLabel ?? null,
    lastMessageText: base.lastMessageText ?? incoming.lastMessageText ?? null,
    whoLast: base.whoLast ?? incoming.whoLast ?? null,
    lastMessageAt: base.lastMessageAt ?? incoming.lastMessageAt ?? null,
    setterName: base.setterName ?? incoming.setterName ?? null,
  };
}

/** The PreviewData contribution of a metadata-only fetch (no messages array):
 *  reply turns, setter, and the owed-wait label derived from the timestamp pair. */
function metaToPartial(meta: ConversationMeta): Partial<PreviewData> {
  const isWaiting = meta.lastLeadMsgAt != null && meta.lastLeadMsgAt > (meta.lastSetterMsgAt ?? "");
  return {
    replyTurns: meta.replyTurns,
    setterName: meta.setterName,
    waitLabel: isWaiting ? formatWait(minutesSince(meta.lastLeadMsgAt!)) : null,
  };
}

/** Map a fetched bundle + its sibling row into the shared PreviewData shape.
 *  waitLabel is set only when the lead genuinely spoke last (an owed wait). */
export function toPreviewData(bundle: ConversationBundle, sibling: SiblingRef): PreviewData {
  const derived = deriveFromMessages(bundle.messages);
  const isWaiting =
    bundle.lastLeadMsgAt != null && bundle.lastLeadMsgAt > (bundle.lastSetterMsgAt ?? "");
  return {
    conversationId: sibling.conversationId,
    contactName: sibling.contactName,
    leadInstagram: sibling.leadInstagram ?? null,
    replyTurns: bundle.replyTurns,
    messageCount: derived.messageCount,
    waitLabel: isWaiting ? formatWait(minutesSince(bundle.lastLeadMsgAt!)) : null,
    lastMessageText: derived.lastMessageText,
    whoLast: derived.whoLast,
    lastMessageAt: derived.lastMessageAt,
    setterName: bundle.setterName,
  };
}

// -----------------------------------------------------------------------------
// Fetch — the SAME two queries the reader runs, plus the wait timestamps.
// Writes setMessagesCache so the reader's fetchMessages is a cache hit next swipe.
// -----------------------------------------------------------------------------
export async function fetchConversationBundle(
  conversationId: string,
  organizationId: string
): Promise<ConversationBundle> {
  const { data: msgData, error: msgErr } = await supabase!
    .from("ghl_messages")
    .select(
      "id, message_text, sent_at, sender_type, has_media, ghl_sender_user_id, sender_name, message_type"
    )
    .eq("conversation_id", conversationId)
    .not("message_type", "in", "(TYPE_ACTIVITY_OPPORTUNITY,TYPE_INTERNAL_COMMENT,TYPE_CALL)")
    .order("sent_at", { ascending: false })
    .limit(MSG_PAGE_SIZE);
  if (msgErr) throw msgErr;

  const filtered = ((msgData || []) as BundleMessage[]).filter(
    (m) =>
      !m.message_text?.includes("Opportunity created") &&
      !m.message_text?.includes("Opportunity updated") &&
      !m.message_text?.includes("Opportunity deleted")
  );
  // Oldest-at-top, newest-at-bottom — same orientation the reader stores.
  const messages = filtered.reverse();
  setMessagesCache(conversationId, organizationId, messages);

  const { data: convData, error: convErr } = await supabase!
    .from("ghl_conversations")
    .select("ghl_assigned_user_id, reply_turns, last_lead_msg_at, last_setter_msg_at")
    .eq("id", conversationId)
    .single();
  if (convErr) throw convErr;

  let setterName: string | null = null;
  if (convData?.ghl_assigned_user_id) {
    const { data: prof } = await supabase!
      .from("profiles")
      .select("full_name")
      .eq("ghl_user_id", convData.ghl_assigned_user_id)
      .single();
    setterName = prof?.full_name ?? null;
  }

  const bundle: ConversationBundle = {
    messages,
    replyTurns: convData?.reply_turns ?? null,
    setterName,
    lastLeadMsgAt: convData?.last_lead_msg_at ?? null,
    lastSetterMsgAt: convData?.last_setter_msg_at ?? null,
  };
  previewCache.set(cacheKey(organizationId, conversationId), { bundle, at: Date.now() });
  notifyPreview(organizationId, conversationId);
  return bundle;
}

// -----------------------------------------------------------------------------
// Metadata-only fetch — ghl_conversations + profiles ONLY, NO ghl_messages.
// For surfaces (P2/P4) whose list row already carries the messages array, so the
// card's derived fields are free; this fills reply turns / setter / wait without
// a ghl_messages request (keeps P2/P4's route-count-0 assertion honest).
// -----------------------------------------------------------------------------
export async function fetchConversationMeta(
  conversationId: string,
  organizationId: string
): Promise<ConversationMeta> {
  const { data, error } = await supabase!
    .from("ghl_conversations")
    .select("ghl_assigned_user_id, reply_turns, last_lead_msg_at, last_setter_msg_at")
    .eq("id", conversationId)
    .single();
  if (error) throw error;

  let setterName: string | null = null;
  if (data?.ghl_assigned_user_id) {
    const { data: prof } = await supabase!
      .from("profiles")
      .select("full_name")
      .eq("ghl_user_id", data.ghl_assigned_user_id)
      .single();
    setterName = prof?.full_name ?? null;
  }

  const meta: ConversationMeta = {
    replyTurns: data?.reply_turns ?? null,
    setterName,
    lastLeadMsgAt: data?.last_lead_msg_at ?? null,
    lastSetterMsgAt: data?.last_setter_msg_at ?? null,
  };
  metaCache.set(cacheKey(organizationId, conversationId), { meta, at: Date.now() });
  notifyPreview(organizationId, conversationId);
  return meta;
}

// -----------------------------------------------------------------------------
// F4 — prefetch the two neighbors on landing. Throttled + cancelable by default
// so a fast ArrowRight held through the list never stacks concurrent queries.
// -----------------------------------------------------------------------------
export function useNeighborPrefetch(
  siblings: NavSibling[] | undefined,
  currentIndex: number | undefined,
  organizationId: string
): void {
  const genRef = useRef(0);
  useEffect(() => {
    if (!siblings || currentIndex == null || !organizationId) return;
    const gen = ++genRef.current;
    // A sibling whose row already carries the message-derived fields needs only
    // the meta path (no ghl_messages); otherwise the full bundle (which also
    // warms setMessagesCache so the swipe-open is a cache hit).
    const derivedKnown = (s: NavSibling) => s.initialPreview?.messageCount != null;
    const targets = [currentIndex - 1, currentIndex + 1]
      .filter((i) => i >= 0 && i < siblings.length)
      .map((i) => siblings[i])
      .filter((s) =>
        derivedKnown(s)
          ? !getCachedMeta(organizationId, s.conversationId)
          : !getCachedBundle(organizationId, s.conversationId)
      );
    if (targets.length === 0) return;
    // Debounce: a held key-repeat clears this timeout on every index change, so
    // only the SETTLED index ever fires a fetch (≤2 in flight).
    const timer = setTimeout(() => {
      if (gen !== genRef.current) return; // superseded during the debounce
      void Promise.allSettled(
        targets.map((s) =>
          derivedKnown(s)
            ? fetchConversationMeta(s.conversationId, organizationId)
            : fetchConversationBundle(s.conversationId, organizationId)
        )
      );
    }, 200);
    return () => clearTimeout(timer);
  }, [siblings, currentIndex, organizationId]);
}

// -----------------------------------------------------------------------------
// usePreviewData — the reactive read a neighbor card renders. NO fetching here;
// useNeighborPrefetch owns the (debounced, cancelable) fetches. This reads the
// module caches, subscribes so it re-renders when a prefetch resolves, and MERGE-
// fills the surface's authoritative `initial` fields over anything derived.
// -----------------------------------------------------------------------------
export function usePreviewData(
  sibling: NavSibling | null,
  organizationId: string,
  initial?: Partial<PreviewData>
): PreviewData | null {
  const [, forceTick] = useState(0);
  const conversationId = sibling?.conversationId;
  useEffect(() => {
    if (!conversationId || !organizationId) return;
    return subscribePreview(organizationId, conversationId, () => forceTick((t) => t + 1));
  }, [conversationId, organizationId]);

  if (!sibling) return null;
  // base = identity + the surface's own authoritative fields (never re-fetched).
  const base = mergeFillNulls(identityPreview(sibling), initial ?? sibling.initialPreview ?? {});
  const bundle = getCachedBundle(organizationId, sibling.conversationId);
  if (bundle) return mergeFillNulls(base, toPreviewData(bundle, sibling));
  const meta = getCachedMeta(organizationId, sibling.conversationId);
  if (meta) return mergeFillNulls(base, metaToPartial(meta));
  return base;
}
